import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import './AddVehicleModal.css';
import cacheStorage from '../../../utils/cacheStorage';

export default function EditVehicleModal({ isOpen, onClose, vehicleData, onEditSuccess }) {
    const [formData, setFormData] = useState({
        plate_no: '',
        model: 'Cargo Van',
        capacity_kg: '',
        capacity_m3: '',
        warehouse_id: '',
        status: 'INACTIVE'
    });
    const [error, setError] = useState(null);

    // Populate form when vehicleData changes
    useEffect(() => {
        if (vehicleData) {
            setFormData({
                plate_no: vehicleData.id || '',
                model: vehicleData.type || 'Cargo Van',
                capacity_kg: vehicleData.capacity_kg ?? '',
                capacity_m3: vehicleData.capacity_m3 ?? '',
                warehouse_id: vehicleData.warehouse_id ?? '',
                status: vehicleData.status || 'INACTIVE'
            });
        }
    }, [vehicleData]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const payload = {
            model: formData.model,
            capacity_kg: formData.capacity_kg ? parseFloat(formData.capacity_kg) : null,
            capacity_m3: formData.capacity_m3 ? parseFloat(formData.capacity_m3) : null,
            warehouse_id: formData.warehouse_id ? parseInt(formData.warehouse_id, 10) : null,
            status: formData.status
        };

        try {
            const token = cacheStorage.getItem('access_token');
            const response = await axios.put(
                `https://smart-cargo-backend-923182991420.us-central1.run.app/api/vehicles/${formData.plate_no}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const v = response.data;
            const updatedVehicle = {
                id: v.plate_no,
                type: v.model,
                capacity_kg: v.capacity_kg ?? null,
                capacity_m3: v.capacity_m3 ?? null,
                warehouse_id: v.warehouse_id ?? null,
                status: v.status || formData.status || 'INACTIVE',
                currentRoute: v.warehouse_id ? `Warehouse ${v.warehouse_id}` : 'N/A',
                assignedCourier: null
            };

            onEditSuccess(updatedVehicle);
            onClose();
        } catch (err) {
            console.error('Failed to update vehicle:', err);
            setError('Failed to update vehicle. Please try again.');
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content add-vehicle-modal">
                <div className="modal-header">
                    <div className="modal-title-group">
                        <h2>Edit Vehicle</h2>
                        <p className="modal-subtitle">Update vehicle details — {formData.plate_no}</p>
                    </div>
                    <button className="modal-close" onClick={onClose} aria-label="Close modal">
                        <X size={20} />
                    </button>
                </div>

                {error && <div className="modal-error">{error}</div>}

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label>Plate No (Vehicle ID)</label>
                        <input value={formData.plate_no} disabled style={{ opacity: 0.6 }} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="model">Vehicle Model</label>
                        <select id="model" name="model" value={formData.model} onChange={handleChange} required>
                            <option value="Cargo Van">Cargo Van</option>
                            <option value="Delivery Truck">Delivery Truck</option>
                            <option value="Box Truck">Box Truck</option>
                            <option value="Heavy Truck">Heavy Truck</option>
                            <option value="Mercedes-Benz Sprinter">Mercedes-Benz Sprinter</option>
                            <option value="Ford Transit">Ford Transit</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="status">Status</label>
                        <select id="status" name="status" value={formData.status} onChange={handleChange}>
                            <option value="ACTIVE">Active</option>
                            <option value="INACTIVE">Inactive</option>
                            <option value="MAINTENANCE">Maintenance</option>
                            <option value="IDLE">Idle</option>
                        </select>
                    </div>

                    <div className="form-row">
                        <div className="form-group half">
                            <label htmlFor="capacity_kg">Capacity (kg)</label>
                            <input
                                type="number"
                                id="capacity_kg"
                                name="capacity_kg"
                                value={formData.capacity_kg}
                                onChange={handleChange}
                                placeholder="e.g. 1500"
                            />
                        </div>
                        <div className="form-group half">
                            <label htmlFor="capacity_m3">Capacity (m³)</label>
                            <input
                                type="number"
                                id="capacity_m3"
                                name="capacity_m3"
                                value={formData.capacity_m3}
                                onChange={handleChange}
                                placeholder="e.g. 15"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="warehouse_id">Warehouse ID</label>
                        <input
                            type="number"
                            id="warehouse_id"
                            name="warehouse_id"
                            value={formData.warehouse_id}
                            onChange={handleChange}
                            placeholder="e.g. 1 (Optional)"
                        />
                    </div>

                    <div className="modal-actions">
                        <button type="submit" className="btn-submit create-vehicle-btn">
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}